import { Key, useCallback, useMemo, useState } from 'react';
import { IProjectLocationSelect } from './types';
import {
  IGetProjectLocationsArgs,
  useGetProjectLocationsQuery,
} from 'redux-store/api/project-location';
import { useFormContext } from 'react-hook-form';
import { useModalState } from 'hooks/useModalState';
import { ISelectController } from 'controllers/select-controller';
import AddRoomModal from '../../../modals/add-room-modal';
import LocationSelect from '../location-select/location-select';

const getDefaultLocationsParams = (projectId: number): IGetProjectLocationsArgs => ({
  projectId,
  limit: 10,
  cursor: undefined,
  search: undefined,
});

export const ProjectLocationSelect = ({
  projectId,
  withImportingLocation,
  withAddingLocation,
  ...rest
}: IProjectLocationSelect) => {
  const form = useFormContext();

  const { name } = rest;

  const [locationsParams, setLocationsParams] = useState<IGetProjectLocationsArgs>(() =>
    getDefaultLocationsParams(projectId)
  );

  // for closing select after creating new value
  const [dynamicKey, setDynamicKey] = useState<number>(1);

  const [isAddRoomModalOpened, handleOpenAddRoomModal, handleCloseAddRoomModal] = useModalState();

  const {
    data: locationsData,
    isFetching: isLocationsFetching,
    refetch: refetchLocations,
  } = useGetProjectLocationsQuery(locationsParams);

  const locationsOptions: ISelectController['options'] = useMemo(() => {
    if (!locationsData?.items?.length) {
      return [];
    }

    return locationsData.items.map(({ id, title }) => ({
      value: id,
      label: title,
    }));
  }, [locationsData?.items?.length]);

  const resetLocationsParams = useCallback(() => {
    setLocationsParams(getDefaultLocationsParams(projectId));
  }, [projectId, getDefaultLocationsParams]);

  const handleAddRoom = useCallback(
    (value: number) => {
      form.setValue(name, value);
      form.clearErrors(name);
      setDynamicKey((prev) => prev + 1);
    },
    [form]
  );

  const addLocationProps: Partial<ISelectController> & { key: Key } = useMemo(
    () => ({
      key: dynamicKey,
      addNewItem: true,
      addBtnLabel: 'Add Item Location',
      onAddNewItem: handleOpenAddRoomModal,
    }),
    [dynamicKey, handleOpenAddRoomModal]
  );

  return (
    <>
      <LocationSelect
        isLocationsFetching={isLocationsFetching}
        options={locationsOptions}
        nextCursor={locationsData?.meta?.nextCursor}
        resetLocationsParams={resetLocationsParams}
        setLocationsParams={setLocationsParams}
        {...(withAddingLocation && addLocationProps)}
        {...rest}
      />
      {isAddRoomModalOpened && (
        <AddRoomModal
          projectId={projectId}
          handleClose={handleCloseAddRoomModal}
          open={isAddRoomModalOpened}
          onAdd={handleAddRoom}
        />
      )}
    </>
  );
};
